import { ApiClient } from '../client';
import { ApiResponse } from '../client';

export interface AnalyticsSummary {
  totalRevenue: number;
  totalUsers: number;
  totalOrders: number;
  totalProducts: number;
  orderGrowth: number;
  revenueGrowth: number;
  conversionRate: number;
  activeUsers: number;
  pendingOrders: number;
}

export interface SalesAnalytics {
  dailySales: Array<{
    date: string;
    revenue: number;
    orders: number;
  }>;
  topProducts: Array<{
    id: string;
    name: string;
    sales: number;
    revenue: number;
  }>;
  paymentMethods: Array<{
    method: string;
    count: number;
    percentage: number;
  }>;
}

export interface ProductAnalytics {
  topSellingProducts: Array<{
    id: string;
    name: string;
    sales: number;
    revenue: number;
    category: string;
  }>;
  categoriesPerformance: Array<{
    id: string;
    name: string;
    salesCount: number;
    revenue: number;
    productCount: number;
  }>;
  lowStockProducts: Array<{
    id: string;
    name: string;
    stock: number;
    category: string;
  }>;
  returnsData: Array<{
    id: string;
    name: string;
    returnsCount: number;
    returnRate: number;
  }>;
}

export interface CustomerAnalytics {
  customerGroups: Array<{
    id: string;
    segment: string;
    count: number;
    percentage: number;
    avgOrderValue: number;
  }>;
  topCustomers: Array<{
    id: string;
    name: string;
    orders: number;
    totalSpent: number;
    lastOrder: string;
  }>;
  acquisitionChannels: Array<{
    channel: string;
    customers: number;
    percentage: number;
  }>;
  retention: {
    rate: number;
    byMonth: Array<{
      month: string;
      newCustomers: number;
      returningCustomers: number;
      rate: number;
    }>;
  };
}

export interface FunnelAnalytics {
  totalVisitors: number;
  totalConversions: number;
  overallConversionRate: number;
  comparisonPeriodRate: number;
  steps: Array<{
    name: string;
    count: number;
    rate: number;
    previousRate: number;
  }>;
  abandonmentByPage: Array<{
    page: string;
    visitors: number;
    abandonmentRate: number;
  }>;
  deviceBreakdown: Array<{
    device: string;
    visitors: number;
    conversions: number;
    rate: number;
  }>;
}

export interface CommercialAnalytics {
  totalSales: number;
  totalOrders: number;
  avgOrderValue: number;
  totalCustomers: number;
  totalPrivateCustomers: number;
  monthlySales: Array<{
    month: string;
    sales: number;
    orders: number;
  }>;
  topCustomers: Array<{
    name: string;
    totalSpent: number;
    orders: number;
  }>;
}

export class AnalyticsService {
  constructor(private client: ApiClient) {}

  /**
   * Obtiene un resumen general de las analíticas
   */
  async getAnalyticsSummary(params: { period: string }): Promise<ApiResponse<AnalyticsSummary>> {
    return this.client.get<AnalyticsSummary>('/analytics/summary', { params });
  }

  /**
   * Obtiene analíticas detalladas de ventas
   */
  async getSalesAnalytics(params: { period: string }): Promise<ApiResponse<SalesAnalytics>> {
    return this.client.get<SalesAnalytics>('/analytics/sales', { params });
  }

  /**
   * Obtiene analíticas detalladas de productos
   */
  async getProductAnalytics(params: { period: string }): Promise<ApiResponse<ProductAnalytics>> {
    return this.client.get<ProductAnalytics>('/analytics/products', { params });
  }

  /**
   * Obtiene analíticas detalladas de clientes
   */
  async getCustomerAnalytics(params: { period: string }): Promise<ApiResponse<CustomerAnalytics>> {
    return this.client.get<CustomerAnalytics>('/analytics/customers', { params });
  }

  /**
   * Obtiene analíticas del embudo de conversión
   */
  async getFunnelAnalytics(params: { period: string }): Promise<ApiResponse<FunnelAnalytics>> {
    return this.client.get<FunnelAnalytics>('/analytics/funnel', { params });
  }

  /**
   * Obtiene analíticas específicas para comerciales
   */
  async getCommercialAnalytics(params?: { period?: string; months?: number }): Promise<ApiResponse<CommercialAnalytics>> {
    return this.client.get<CommercialAnalytics>('/analytics/commercial', { params });
  }
} 